import { useEffect, useState } from 'react';
import type { ContactFormFields } from '@transcend-io/contact-form-schema';
import { snakeCase } from 'change-case';
import { AnimatePresence, motion } from 'framer-motion';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';
import type { Schema } from 'zod';

import type { ResponseError } from '#/lib/api/types';
import {
  ChiliPiperProvider,
  SchedulingStateEnum,
  useChiliPiperModal,
} from '#/lib/chili-piper/ChiliPiperContext';
import { checkFormServerError } from '#/lib/contact-form-client/helpers';
import { submitLead } from '#/lib/contact-form-client/submit';
import type { SanityMultiForm } from '#/lib/sanity/queries/form';
import { useAnalytics, useConversionEvent } from '#/lib/segment';
import { cn } from '#/lib/utils';
import { Button } from '#/components/Button';

import LoadingSpinner from '../LoadingSpinner';
import { FormFields } from './Custom/FormFields';
import { useFormMetadata } from './utils';

type Props<T extends Schema<ContactFormFields>> = Pick<
  SanityMultiForm,
  | 'pardotCampaignId'
  | 'pardotListIds'
  | 'trackingEventName'
  | 'enableScheduling'
  | 'submitButtonLabel'
  | 'fieldGroups'
> & {
  formFieldsSchema: T;
  ConfirmationComponent: React.FC<{ isWaitingOnSchedulingLoad: boolean }>;
  onSubmitSuccess?: () => void;
  onSubmitError?: ({ error }: ResponseError) => void;
  children: (ctx: UseFormReturn<ContactFormFields>) => React.ReactNode;
};

function RawForm<T extends Schema<ContactFormFields>>({
  pardotCampaignId,
  pardotListIds,
  formFieldsSchema,
  trackingEventName,
  ConfirmationComponent,
  onSubmitSuccess,
  onSubmitError,
  children,
  fieldGroups,
  submitButtonLabel,
  enableScheduling,
}: Props<T>): JSX.Element {
  const { initSchedulingModal, waitForSchedulingLoad, schedulingState } =
    useChiliPiperModal();
  const [currentStep, setCurrentStep] = useState(0);
  const [stepEventsFired, setStepEventsFired] = useState<number[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [formSubmissionId, setFormSubmissionId] = useState<string | undefined>(
    undefined,
  );
  const { methods, getMetadata } = useFormMetadata({
    pardotCampaignId,
    pardotListIds,
    formFieldsSchema,
  });

  const analytics = useAnalytics();
  const fireConversionEvent = useConversionEvent();

  useEffect(() => methods.setValue('consent', true), [methods]);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const isLastStep = currentStep === fieldGroups.length - 1;
  const isFirstStep = currentStep === 0;

  const currentStepFields = fieldGroups[currentStep].fields
    .flatMap((field) => {
      if ('name' in field && field.name) {
        return [field.name];
      }
      if (field._type === 'countryRegion') {
        return ['country', 'state'];
      }
    })
    .filter((fieldName): fieldName is keyof ContactFormFields =>
      Boolean(fieldName),
    );

  /**
   * Handle back button click, which will also clear errors so it doesnt silently block submissions of the form on previous steps
   */
  const handleBack = async () => {
    // Trigger validation silently without focusing on errors
    const isValid = await methods.trigger(currentStepFields, {
      shouldFocus: false,
    });

    if (!isValid) {
      // Get errored fields in the current step
      const errors = methods.formState.errors;
      const erroredFieldsInCurrentStep = currentStepFields.filter(
        (fieldName) => errors[fieldName],
      );

      erroredFieldsInCurrentStep.forEach((fieldName) => {
        methods.resetField(fieldName, {
          keepError: false,
          keepDirty: false,
          keepTouched: false,
        });
      });
      setCurrentStep(currentStep - 1);
    }
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit: SubmitHandler<ContactFormFields> = async (fields) => {
    try {
      // Fire event for the current step if it hasn't been fired yet, this will stop duplicate events from being fired if a user steps back in the form
      if (!stepEventsFired.includes(currentStep)) {
        fireConversionEvent(fields.email, 'Contact Form Step Submitted', {
          step: currentStep + 1,
          totalSteps: fieldGroups.length,
          isFinalStep: isLastStep,
        });
        setStepEventsFired([...stepEventsFired, currentStep]);
      }
      if (isLastStep) {
        fireConversionEvent(fields.email, trackingEventName);
      }

      const recaptchaToken =
        executeRecaptcha &&
        (await executeRecaptcha(snakeCase(trackingEventName)));
      const metadata = {
        ...getMetadata(recaptchaToken),
        step: currentStep + 1,
        totalSteps: fieldGroups.length,
      };

      if (enableScheduling && isLastStep) {
        initSchedulingModal(fields);
      }

      const response = await submitLead(
        { ...fields, submissionId: formSubmissionId },
        metadata,
        { analytics },
      );

      if ('error' in response) {
        console.error(response.error);
        methods.setError('root', {
          type: 'server',
          message: response.error,
        });
        const serverError = checkFormServerError(methods.formState);
        if (serverError) {
          onSubmitError?.({ error: serverError.message });
        }
      } else {
        if (isLastStep) {
          setShowConfirmation(true);
          if (enableScheduling) {
            await waitForSchedulingLoad();
          }
          onSubmitSuccess?.();
        } else {
          setFormSubmissionId(response.submissionId);
          setCurrentStep((prev) => prev + 1);
        }
      }
    } catch (err) {
      console.error(err);
      methods.setError('root', { type: 'server' });
      const serverError = checkFormServerError(methods.formState);
      if (serverError) {
        onSubmitError?.({ error: serverError.message });
      }
    }
  };

  const buttonLabel = isLastStep ? submitButtonLabel : 'Next';

  return (
    <AnimatePresence mode="popLayout">
      {methods.formState.isSubmitSuccessful && showConfirmation ? (
        <motion.div
          key="confirmation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <ConfirmationComponent
            isWaitingOnSchedulingLoad={
              enableScheduling &&
              schedulingState.status === SchedulingStateEnum.ROUTING
            }
          />
        </motion.div>
      ) : (
        <>
          <div className="flex gap-12 items-center justify-between mb-10 l:mb-20 col-span-full">
            {fieldGroups[currentStep].title ? (
              <h3 className="text-18 font-[440]">
                {fieldGroups[currentStep].title}
              </h3>
            ) : null}
            <span className="font-mono text-grayscale-04 text-12 font-[400]">
              STEP{' '}
              <motion.span
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1 }}
                exit={{ y: -10, opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                {currentStep + 1}
              </motion.span>
              /{fieldGroups.length}
            </span>
          </div>
          <motion.div
            key={`form-step-${currentStep}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col justify-between"
          >
            <FormProvider {...methods}>
              <form
                onSubmit={methods.handleSubmit(handleSubmit)}
                className="grid grid-cols-2 gap-20"
              >
                <FormFields
                  autoFocusFirstField={!isFirstStep}
                  fields={fieldGroups[currentStep].fields}
                  control={methods.control}
                />

                <div className="col-span-full grid grid-cols-2 gap-20">
                  {!isFirstStep && (
                    <Button
                      variant="text"
                      onClick={handleBack}
                      type="button"
                      className="col-span-1"
                    >
                      Back
                    </Button>
                  )}
                  <Button
                    variant="form"
                    type="submit"
                    disabled={methods.formState.isSubmitting}
                    className={cn(
                      'grid place-items-center col-span-1',
                      isFirstStep && 'col-start-2',
                    )}
                  >
                    <span
                      className={cn(
                        'row-start-1 col-start-1',
                        methods.formState.isSubmitting && 'opacity-0',
                      )}
                    >
                      {buttonLabel}
                    </span>
                    {methods.formState.isSubmitting && (
                      <LoadingSpinner
                        className={cn(
                          'row-start-1 col-start-1 absolute size-22',
                        )}
                      />
                    )}
                  </Button>
                </div>
              </form>
              {/* Legal Text And Error Message */}
              {children(methods)}
            </FormProvider>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

export function MultiStepForm<T extends Schema<ContactFormFields>>(
  props: Props<T>,
) {
  return (
    <ChiliPiperProvider enableScheduling={props.enableScheduling}>
      <RawForm {...props} />
    </ChiliPiperProvider>
  );
}
